import React from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import es from 'date-fns/locale/es';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import ThreeWeekView, { ThreeWeekEnumContext } from './ThreeWeekView';
import MonthFlatView from './MonthFlatView';
import { AUTO_COLORS } from '../utils/colorLegend';
import { buildEnumCache } from '../utils/eventEnumeration';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './CalendarStyles.css';

const locales = { 'es': es };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });
const DnDCalendar = withDragAndDrop(Calendar);

// Auto-colors by position in course schedule (definidos en utils/colorLegend.js)

const todayMidnight = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

const to12h = (date) => {
    const h = date.getHours();
    const m = date.getMinutes();
    const ampm = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 || 12;
    return m > 0 ? `${h12}:${String(m).padStart(2, '0')}${ampm}` : `${h12}${ampm}`;
};

// Determine auto-color based on enumeration
const getAutoColor = (event, enumCache) => {
    if (event._manualColor) return event.color;

    const info = enumCache[event.id];
    if (!info) return AUTO_COLORS.normal;

    if (info.type === 'MC') {
        if (info.total === 1) return AUTO_COLORS.firstMC;
        if (info.num === 1) return AUTO_COLORS.firstMC;        // verde - inicio MC del grupo
        if (info.num === info.total) return AUTO_COLORS.lastMC; // celeste - fin MC del grupo
    } else {
        if (info.total === 1) return AUTO_COLORS.firstCL;
        if (info.num === 1) return AUTO_COLORS.firstCL;        // amarillo - inicio CL del grupo
        if (info.num === info.total) return AUTO_COLORS.lastCL; // naranja - fin CL del grupo
    }
    return AUTO_COLORS.normal; // gris - intermedias
};

// Hidden toolbar that exposes navigation to parent
const HiddenToolbar = ({ label, onNavigate, onView, view }) => {
    const { setLabel, setNav, setViewFn, setCurrentView } = React.useContext(CalToolbarContext);
    React.useEffect(() => {
        setLabel(label);
        setNav(() => (action) => onNavigate(action));
        setViewFn(() => (v) => onView(v));
        setCurrentView(view);
    }, [label, view]);
    return null; // Hidden - parent renders the UI
};

const CalToolbarContext = React.createContext({});

const DragDropCalendar = ({ events, courses = [], onEventDrop, onEventResize, onSelectEvent, onSelectSlot, onRangeChange, highlightInstructorId, highlightCourseId, onToolbarReady, onVisibleLabel, initialView, initialDate }) => {

    // Build enumeration cache with course group sizes
    const enumCache = React.useMemo(() => buildEnumCache(events, courses), [events, courses]);

    // Wheel navigation for month and 3-week views
    const calContainerRef = React.useRef(null);
    const navRef = React.useRef(null);
    const currentViewRef = React.useRef('month');
    const wheelCooldown = React.useRef(false);

    // Wheel handler removido: las vistas mes/3semanas son scroll-infinito y la rueda
    // hace scroll natural a través de las semanas pre-renderizadas (24 semanas centradas en `date`).

    // ── Event style getter ──
    const eventStyleGetter = (event) => {
        const isPast = (event.end instanceof Date ? event.end : new Date(event.end)) < new Date();
        const isGcal = event.source === 'google_calendar';
        const isCancelled = event.status === 'cancelled';
        const evInstructorId = event.resource?.instructorId || event.instructorId;
        const evCourseId = event.resource?.courseId || event.courseId;
        const matchesInstructor = !highlightInstructorId || evInstructorId === highlightInstructorId;
        const matchesCourse = !highlightCourseId || evCourseId === highlightCourseId;
        const anyFilterActive = !!(highlightInstructorId || highlightCourseId);
        const isHighlighted = anyFilterActive && matchesInstructor && matchesCourse;
        const isDimmed = anyFilterActive && !isHighlighted && !isGcal;
        const dotColor = isCancelled ? '#ef4444' : getAutoColor(event, enumCache);

        return {
            className: isDimmed ? 'gcal-evt-dimmed' : isHighlighted ? 'gcal-evt-highlighted' : '',
            style: {
                backgroundColor: isCancelled ? '#fef2f2' : 'transparent',
                borderRadius: '4px',
                borderLeft: `3px solid ${dotColor}`,
                opacity: isCancelled ? 0.5 : (isPast ? 0.5 : 1),
                color: isCancelled ? '#b91c1c' : (isPast ? '#9ca3af' : '#3c4043'),
                fontSize: '12px',
                padding: '1px 4px 1px 4px',
                lineHeight: '20px',
                fontWeight: '500',
                cursor: 'pointer',
                textDecoration: isCancelled ? 'line-through' : 'none',
            }
        };
    };

    // ── Custom Event Component (dot + enumerated title) ──
    const CustomEvent = ({ event }) => {
        const dotColor = getAutoColor(event, enumCache);
        const startD = event.start instanceof Date ? event.start : new Date(event.start);
        const timeStr = to12h(startD);
        const info = enumCache[event.id];
        // Build display title with enumeration
        const courseAbrev = event.resource?.courseAbreviatura || event.courseAbreviatura;
        const courseName = courseAbrev || event.resource?.courseName || '';
        // Asesor first name for MC events
        const asesorShort = (event.type === 'masterclass' || event.resource?.type === 'masterclass')
            ? (event.asesorName || '').split(' ')[0] : '';
        let displayTitle;
        if (info && courseName) {
            displayTitle = `${info.num} ${info.type} ${courseName}${asesorShort ? ` - ${asesorShort}` : ''}`;
        } else if (info) {
            displayTitle = `${info.num} ${info.type} ${event.title || ''}${asesorShort ? ` - ${asesorShort}` : ''}`;
        } else {
            displayTitle = event.title || '';
        }

        return (
            <div className="flex items-center gap-1 overflow-hidden whitespace-nowrap" title={`${timeStr} ${event.title || ''} (${info ? `${info.num}/${info.total}` : ''})`}>
                <span className="shrink-0 w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: dotColor }} />
                <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 shrink-0">{timeStr}</span>
                <span className="text-[12px] font-semibold text-gray-800 dark:text-gray-200 truncate">{displayTitle}</span>
            </div>
        );
    };

    // ── Day cell background (month view) ──
    const dayPropGetter = (date) => {
        const today = todayMidnight();
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        if (d.getTime() === today.getTime()) {
            return { className: 'gcal-today', style: { backgroundColor: '#f0f6ff' } };
        }
        if (d < today) {
            return { className: 'gcal-past', style: { backgroundColor: '#fafafa' } };
        }
        return {};
    };

    // ── Custom date number (month view) ──
    const CustomDateHeader = ({ date, label }) => {
        const today = todayMidnight();
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        const isToday = d.getTime() === today.getTime();
        const isPast = d < today;
        return (
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 6px 2px' }}>
                <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '26px', height: '26px', borderRadius: '50%',
                    fontSize: '12px', fontWeight: isToday ? 700 : 500,
                    backgroundColor: isToday ? '#1a73e8' : 'transparent',
                    color: isToday ? '#fff' : isPast ? '#b0b3b8' : '#3c4043',
                    cursor: 'pointer',
                }}>
                    {label}
                </span>
            </div>
        );
    };

    // ── 12h time format ──
    const formats = {
        timeGutterFormat: (date) => to12h(date),
        eventTimeRangeFormat: ({ start, end }) => `${to12h(start)} - ${to12h(end)}`,
        agendaTimeRangeFormat: ({ start, end }) => `${to12h(start)} - ${to12h(end)}`,
        dayHeaderFormat: (date) => format(date, "EEEE d 'de' MMMM", { locale: es }),
        dayRangeHeaderFormat: ({ start, end }) => `${format(start, "d MMM", { locale: es })} — ${format(end, "d MMM yyyy", { locale: es })}`,
    };

    const draggableAccessor = (event) => event.source !== 'google_calendar';
    const resizableAccessor = (event) => event.source !== 'google_calendar';

    const [tbLabel, setTbLabel] = React.useState('');
    const [tbNav, setTbNav] = React.useState(null);
    const [tbViewFn, setTbViewFn] = React.useState(null);
    const [tbView, setTbView] = React.useState('month');

    // Controlled date & view for synced navigation between views
    const [controlledDate, setControlledDate] = React.useState(initialDate || new Date());
    const [controlledView, setControlledView] = React.useState(initialView || 'month');

    React.useEffect(() => {
        if (onToolbarReady) onToolbarReady({ label: tbLabel, nav: tbNav, viewFn: tbViewFn, view: tbView });
        // Sync refs for wheel navigation
        navRef.current = tbNav;
        currentViewRef.current = tbView;
    }, [tbLabel, tbNav, tbViewFn, tbView]);

    const viewLabels = { month: 'Mes', '3semanas': '3 Semanas', week: 'Semana', day: 'Día', agenda: 'Agenda' };

    React.useEffect(() => {
        if (onToolbarReady) onToolbarReady({ label: tbLabel, nav: tbNav, viewFn: tbViewFn, view: tbView, viewLabel: viewLabels[tbView] || tbView });
        currentViewRef.current = tbView;
    }, [tbLabel, tbView]);

    return (
        <CalToolbarContext.Provider value={{ setLabel: setTbLabel, setNav: setTbNav, setViewFn: setTbViewFn, setCurrentView: setTbView }}>
            <ThreeWeekEnumContext.Provider value={{ cache: enumCache, getAutoColor, onEventDrop, onVisibleLabel }}>
                <div
                    ref={calContainerRef}
                    className="h-full bg-white dark:bg-[#2e323b] rounded-xl overflow-hidden compact-calendar"
                    style={{ boxShadow: '0 1px 3px rgba(60,64,67,.12), 0 1px 2px rgba(60,64,67,.08)' }}
                >
                    <DnDCalendar
                        localizer={localizer}
                        events={events}
                        onEventDrop={onEventDrop}
                        onEventResize={onEventResize}
                        onSelectEvent={onSelectEvent}
                        onSelectSlot={onSelectSlot}
                        selectable
                        resizable
                        date={controlledDate}
                        view={controlledView}
                        onNavigate={(newDate) => {
                            const d = newDate instanceof Date ? newDate : new Date(newDate);
                            // Force re-render for agenda "Today" by creating a new Date object always
                            setControlledDate(new Date(d.getTime()));
                        }}
                        onView={(newView) => { setControlledView(newView); }}
                        views={{
                            month: MonthFlatView,
                            '3semanas': ThreeWeekView,
                            week: true,
                            day: true,
                            agenda: true,
                        }}
                        step={30}
                        showMultiDayTimes
                        culture="es"
                        popup
                        popupOffset={{ x: 0, y: 8 }}
                        dayPropGetter={dayPropGetter}
                        eventPropGetter={eventStyleGetter}
                        draggableAccessor={draggableAccessor}
                        resizableAccessor={resizableAccessor}
                        onRangeChange={onRangeChange}
                        formats={formats}
                        components={{
                            month: { dateHeader: CustomDateHeader },
                            event: CustomEvent,
                            toolbar: HiddenToolbar,
                        }}
                        toolbar={true}
                        messages={{
                            next: '›',
                            previous: '‹',
                            today: 'Hoy',
                            month: 'Mes',
                            '3semanas': '3 Semanas',
                            week: 'Semana',
                            day: 'Día',
                            agenda: 'Agenda',
                            date: 'Fecha',
                            time: 'Hora',
                            event: 'Evento',
                            noEventsInRange: 'No hay eventos en este rango.',
                            showMore: total => `+${total} más`,
                        }}
                        style={{ height: '100%' }}
                    />
                </div>
            </ThreeWeekEnumContext.Provider>
        </CalToolbarContext.Provider>
    );
};

export default DragDropCalendar;
